/**
 * @file The exchange day component allows users to propose exchanging entire days to a colleague.
 * @author Maarten Lensink
 */
import { OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { BackendSettingsService } from '../../backend-settings.service';
import { ResourceService } from '../../resource.service';
import { AlertsService } from '../../core/alerts.service';
import { LocalisationService } from '../../core/localisation.service';
import { OverlayService } from '../../core/overlay.service';
import { OverlayProvider } from '../../ui/overlay-provider';
import { NotificationsService } from '../notifications.service';
import { RequestTypes } from '../request-types';
import { RequestStatus } from '../request-status';
/**
 * The component allows the user to suggest exchanging a day's work.
 * @export
 * @class ExchangeDayComponent
 * @implements {OnInit}
 */
var ExchangeDayComponent = /** @class */ (function () {
    /**
     * Creates an instance of ExchangeDayComponent.
     * @param {NotificationsService} notificationsService
     * @param {AlertsService} alertsService
     * @memberOf ExchangeDayComponent
     */
    function ExchangeDayComponent(notificationsService, alertsService, overlayService, overlayProvider, resourceService, translateService, backendSettingsService, localisationService) {
        this.notificationsService = notificationsService;
        this.alertsService = alertsService;
        this.overlayService = overlayService;
        this.overlayProvider = overlayProvider;
        this.resourceService = resourceService;
        this.translateService = translateService;
        this.backendSettingsService = backendSettingsService;
        this.localisationService = localisationService;
        /** The targetItems that is being received from a child component */
        this.targetItems = [];
        this.formConfiguration = overlayProvider.configuration;
        if (overlayProvider.configuration.components) {
            this.targetConfiguration = overlayProvider.configuration.components[0].configuration;
        }
    }
    Object.defineProperty(ExchangeDayComponent.prototype, "requestId", {
        /**
         * Gets the Id of the current request.
         * Breeze.js does not allow an Id to be defined.
         * AOT does not allow it in the template.
         * @readonly
         * @type {number}
         * @memberOf ExchangeDayComponent
         */
        get: function () {
            if (!this.request) {
                return -1;
            }
            return this.request['Id'];
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Sets up the request.
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.ngOnInit = function () {
        this.setRequest();
    };
    /**
     * Destroys the subscription on the request.
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.ngOnDestroy = function () {
        if (this.request && this.requestSubscription != null) {
            this.request.entityAspect.propertyChanged.unsubscribe(this.requestSubscription);
        }
    };
    /**
     * Returns a value indicating whether the request is ready to be saved.
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.canSave = function () {
        if (!this.request) {
            return false;
        }
        if (!this.request.ReceiverOid && !this.request.TargetDay) {
            return false;
        }
        var isExpired = this.backendSettingsService.isExpired(this.request.TargetDay);
        if (isExpired) {
            return false;
        }
        if (this.targetItems.length <= 0) {
            return false;
        }
        return true;
    };
    /**
     * Cancels the request and closes the overlay.
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.cancelItem = function () {
        this.request.entityAspect.rejectChanges();
        this.overlayService.closeOverlay();
    };
    /**
     * Saves the new created exchange request when valid.
     * @param {any} event The click event that triggered the method.
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.saveItem = function (event) {
        var _this = this;
        // These are extra checks, because the send button ought to be disabled already.
        if (!this.request.ReceiverOid) {
            this.alertsService.createAlert('warning', 'APP.NOTIFICATIONS.EXCHANGE_PICK_RECEIVER');
            return;
        }
        if (!this.request.TargetDay) {
            this.alertsService.createAlert('warning', 'APP.NOTIFICATIONS.EXCHANGE_PICK_DAY');
            return;
        }
        if (!this.backendSettingsService.isDayOnTime(moment(this.request.SubjectDay))) {
            this.alertsService.createAlert('warning', 'APP.NOTIFICATIONS.TOO_LATE');
            return;
        }
        if (event) {
            event.target.classList.add('sym-working');
        }
        this.notificationsService.symptomManagementIssue1319();
        this.notificationsService.saveChanges(function (error) {
            // Remove sym-working to show that action is finished.
            if (event) {
                event.target.classList.remove('sym-working');
            }
            // Give correct alert based on whether an error is given or not.
            if (error) {
                _this.alertsService.createAlert('warning', 'APP.NOTIFICATIONS.REQUEST_UNSUCCESSFULLY_SENT');
            }
            else {
                _this.alertsService.createAlert('success', 'APP.NOTIFICATIONS.REQUEST_SUCCESSFULLY_SENT');
                _this.overlayService.closeOverlay();
            }
        });
    };
    /**
     * Sets up the request.
     * @private
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.setRequest = function () {
        var _this = this;
        this.request = this.notificationsService.getEntityType('Request').createEntity();
        this.request.SubjectDay = moment(this.overlayProvider.entity['Start']).startOf('day').toDate();
        this.request.Type = RequestTypes.ExchangeDay;
        this.request.Status = RequestStatus.Request;
        this.request.SenderOid = this.resourceService.getResourceOid();
        this.updateTitle(this.request);
        this.notificationsService.addEntity(this.request);
        this.requestSubscription = this.request.entityAspect.propertyChanged.subscribe(function (args) {
            if (args.propertyName === 'TargetDay') {
                _this.updateTitle(args.entity);
            }
        });
    };
    /**
     * (Pre)sets the title of the request.
     * @private
     * @param {Request} request
     * @memberOf ExchangeDayComponent
     */
    ExchangeDayComponent.prototype.updateTitle = function (request) {
        var title = this.translateService.instant('APP.NOTIFICATIONS.TYPE_EXCHANGE');
        var dateString = moment(this.request.SubjectDay).format(this.localisationService.date.format);
        title += " " + dateString;
        if (request.TargetDay) {
            dateString = moment(this.request.TargetDay).format(this.localisationService.date.format);
            title += " " + this.translateService.instant('APP.NOTIFICATIONS.FOR') + " " + dateString;
        }
        request.Title = title;
    };
    /**
     * Set the targetItems that is received from the child component
     * @param {Entity[]}items
     */
    ExchangeDayComponent.prototype.setTargetItems = function (items) {
        this.targetItems = items;
    };
    return ExchangeDayComponent;
}());
export { ExchangeDayComponent };
